import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Ticket,
  CheckSquare,
  FolderKanban,
  BookOpen,
  Users,
  BarChart3,
  Columns3,
  LayoutGrid,
  Server,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SimpleTooltip } from '@/components/ui';

// --- Types ---

interface NavItemDef {
  path: string;
  icon: LucideIcon;
  label: string;
  internalOnly?: boolean;
}

interface NavGroupDef {
  label: string;
  items: NavItemDef[];
}

// --- Navigation config ---

const topItem: NavItemDef = {
  path: '/dashboard',
  icon: LayoutDashboard,
  label: 'Dashboard',
};

const navGroups: NavGroupDef[] = [
  {
    label: 'SOPORTE',
    items: [
      { path: '/conversaciones', icon: MessageSquare, label: 'Conversaciones' },
      { path: '/tickets', icon: Ticket, label: 'Tickets' },
    ],
  },
  {
    label: 'OPERACIONES',
    items: [
      { path: '/actividades', icon: CheckSquare, label: 'Actividades', internalOnly: true },
      { path: '/proyectos', icon: FolderKanban, label: 'Proyectos', internalOnly: true },
      { path: '/kanban', icon: Columns3, label: 'Kanban', internalOnly: true },
      { path: '/clientes', icon: Users, label: 'Clientes', internalOnly: true },
      { path: '/cmdb', icon: Server, label: 'Infraestructura', internalOnly: true },
    ],
  },
  {
    label: 'RECURSOS',
    items: [
      { path: '/conocimiento', icon: BookOpen, label: 'Conocimiento' },
      { path: '/reportes', icon: BarChart3, label: 'Reportes', internalOnly: true },
      { path: '/dashboards', icon: LayoutGrid, label: 'Dashboards', internalOnly: true },
    ],
  },
];

const footerItem: NavItemDef = {
  path: '/configuracion',
  icon: Settings,
  label: 'Configuración',
  internalOnly: true,
};

// --- Sub-components ---

function NavItem({
  item,
  collapsed,
  badge,
}: {
  item: NavItemDef;
  collapsed: boolean;
  badge?: number;
}) {
  const link = (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg text-sm font-medium transition-colors',
          collapsed ? 'justify-center p-2' : 'px-3 py-2',
          isActive
            ? 'border-l-2 border-primary bg-primary/10 text-primary'
            : 'border-l-2 border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )
      }
    >
      <item.icon className={cn('h-5 w-5 shrink-0', !collapsed && 'mr-3')} />
      {!collapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {badge != null && badge > 0 && (
            <span className="ml-auto inline-flex items-center justify-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <SimpleTooltip content={item.label} side="right">
        {link}
      </SimpleTooltip>
    );
  }

  return link;
}

function NavGroup({
  group,
  collapsed,
  isInternal,
  badges,
}: {
  group: NavGroupDef;
  collapsed: boolean;
  isInternal: boolean;
  badges: Record<string, number>;
}) {
  const visibleItems = group.items.filter(
    (item) => !item.internalOnly || isInternal
  );

  if (visibleItems.length === 0) return null;

  return (
    <div>
      {!collapsed && (
        <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          {group.label}
        </p>
      )}
      <div className="space-y-0.5">
        {visibleItems.map((item) => (
          <NavItem
            key={item.path}
            item={item}
            collapsed={collapsed}
            badge={badges[item.path]}
          />
        ))}
      </div>
    </div>
  );
}

// --- Main sidebar ---

interface QSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  isOpen: boolean;
  onClose: () => void;
  isInternal: boolean;
  badges?: Record<string, number>;
}

export function QSidebar({
  collapsed,
  onToggle,
  isOpen,
  onClose,
  isInternal,
  badges = {},
}: QSidebarProps) {
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        aria-label="Navegación principal"
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border bg-background transition-all duration-200 ease-in-out',
          collapsed ? 'w-16' : 'w-64',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0'
        )}
      >
        {/* Logo + collapse toggle */}
        <div className="flex h-16 items-center justify-between border-b border-border px-3">
          <NavLink to="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary">
              <span className="text-lg font-bold text-primary-foreground">Q</span>
            </div>
            {!collapsed && (
              <span className="text-xl font-bold text-foreground">QOps</span>
            )}
          </NavLink>

          {/* Mobile close */}
          <button
            aria-label="Cerrar menú"
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-accent lg:hidden"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>

          {/* Desktop collapse toggle */}
          {collapsed ? (
            <SimpleTooltip content="Expandir sidebar" side="right">
              <button
                aria-label="Expandir sidebar"
                onClick={onToggle}
                className="hidden rounded-lg p-1 hover:bg-accent lg:block"
              >
                <PanelLeftOpen className="h-4 w-4 text-muted-foreground" />
              </button>
            </SimpleTooltip>
          ) : (
            <button
              aria-label="Colapsar sidebar"
              onClick={onToggle}
              className="hidden rounded-lg p-1 hover:bg-accent lg:block"
            >
              <PanelLeftClose className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {/* Dashboard — ungrouped, always visible */}
          <NavItem item={topItem} collapsed={collapsed} />

          {/* Grouped items with separators */}
          {navGroups.map((group) => (
            <div key={group.label}>
              <div className="my-2" />
              <NavGroup
                group={group}
                collapsed={collapsed}
                isInternal={isInternal}
                badges={badges}
              />
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-border p-2">
          {isInternal && (
            <NavItem item={footerItem} collapsed={collapsed} />
          )}
          {!collapsed && (
            <p className="mt-1 px-3 text-[10px] text-muted-foreground/50">
              v{__APP_VERSION__} ({__BUILD_HASH__})
            </p>
          )}
        </div>
      </aside>
    </>
  );
}

// Re-export config for CommandPalette
export { navGroups, topItem, footerItem };
export type { NavItemDef, NavGroupDef };
